import React from "react";
import { View, StyleSheet, Text } from "react-native";
import AxioGeneralModal from "../../axio-ds/Modal/AxioModal";
import AxioInput from "../../axio-ds/Input/AxioInput";
import AxioButton from "../../axio-ds/Button/AxioButton";
import ActivityCard, { ActivityCardProps } from "../ActivityCard/ActivityCard";
import { colors, spacing } from "../../../styles/design-system";

export interface ThreadActivity extends ActivityCardProps {
  id: string;
}

export interface AssignmentActivityGroup {
  id: string;
  courseLabel: string;
  activities: ThreadActivity[];
}

export interface CreateThreadModalProps {
  visible: boolean;
  onRequestClose: () => void;

  customThreadName: string;
  onChangeCustomThreadName: (value: string) => void;
  onCreateCustomThread: () => void;

  taskActivities: ThreadActivity[];
  assignmentGroups: AssignmentActivityGroup[];
  selectedActivityId?: string;
  onSelectActivity?: (id: string) => void;
}

const CreateThreadModal: React.FC<CreateThreadModalProps> = ({
  visible,
  onRequestClose,
  customThreadName,
  onChangeCustomThreadName,
  onCreateCustomThread,
  taskActivities,
  assignmentGroups,
  selectedActivityId,
  onSelectActivity,
}) => {
  const handleSelect = (id: string) => {
    if (onSelectActivity) {
      onSelectActivity(id);
    }
  };

  return (
    <AxioGeneralModal
      visible={visible}
      onRequestClose={onRequestClose}
      title="Create New Thread"
      subtitle="Start a custom thread or create one from an existing task or assignment.">
      <View style={styles.container}>
        {/* Custom thread section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Custom Thread</Text>
          <View style={styles.customRow}>
            <View style={styles.customInputWrapper}>
              <AxioInput
                placeholder="Thread name (optional)"
                value={customThreadName}
                onChangeText={onChangeCustomThreadName}
              />
            </View>
            <AxioButton label="Create" variant="primary" size="md" onPress={onCreateCustomThread} />
          </View>
          <Text style={styles.helperText}>Leave blank to auto-generate a name from your first message.</Text>
        </View>

        {/* Tasks */}
        {taskActivities.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>From Task</Text>
            {taskActivities.map((activity) => (
              <ActivityCard
                key={activity.id}
                {...activity}
                type={activity.type ?? "task"}
                isSelected={activity.id === selectedActivityId}
                onPress={() => handleSelect(activity.id)}
              />
            ))}
          </View>
        )}

        {/* Assignments */}
        {assignmentGroups.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionLabel}>From Assignment</Text>
            {assignmentGroups.map((group) => (
              <View key={group.id} style={styles.assignmentGroup}>
                <Text style={styles.assignmentCourseLabel}>{group.courseLabel.toUpperCase()}</Text>
                {group.activities.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    {...activity}
                    type={activity.type ?? "assignment"}
                    isSelected={activity.id === selectedActivityId}
                    onPress={() => handleSelect(activity.id)}
                  />
                ))}
              </View>
            ))}
          </View>
        )}
      </View>
    </AxioGeneralModal>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: spacing.sm,
    gap: spacing.lg,
  },
  section: {},
  sectionTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: colors.grey[700],
    marginBottom: spacing.xs,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.grey[700],
    marginBottom: spacing.xs,
  },
  assignmentGroup: {
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },
  assignmentCourseLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: colors.grey[500],
    textTransform: "uppercase",
    marginBottom: spacing.xs,
  },
  customRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  customInputWrapper: {
    flex: 1,
  },
  helperText: {
    marginTop: spacing.xs,
    fontSize: 12,
    color: colors.grey[500],
  },
});

export default CreateThreadModal;
